import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { submitQuiz } from "@/lib/api";

const QUESTIONS = [
  {
    q: "How does your team currently use AI?",
    options: [
      { label: "Not at all", score: 0 },
      { label: "Occasionally, ad hoc", score: 1 },
      { label: "Regularly, in some tasks", score: 2 },
      { label: "Systematically, across the team", score: 3 },
    ],
  },
  {
    q: "Do you have documented AI workflows or prompts?",
    options: [
      { label: "None", score: 0 },
      { label: "A few, saved informally", score: 1 },
      { label: "Several, shared with the team", score: 2 },
      { label: "A full, reusable library", score: 3 },
    ],
  },
  {
    q: "How much time is lost to repetitive admin each week?",
    options: [
      { label: "A lot, it is a real drain", score: 0 },
      { label: "A fair amount", score: 1 },
      { label: "Some, but manageable", score: 2 },
      { label: "Very little, we are efficient", score: 3 },
    ],
  },
  {
    q: "How confident is your team using AI tools?",
    options: [
      { label: "Not confident", score: 0 },
      { label: "Curious but unsure", score: 1 },
      { label: "Fairly confident", score: 2 },
      { label: "Very confident", score: 3 },
    ],
  },
  {
    q: "Do you have any AI policy or usage guidance?",
    options: [
      { label: "No", score: 0 },
      { label: "We are considering it", score: 1 },
      { label: "A rough draft", score: 2 },
      { label: "Yes, clear guidance", score: 3 },
    ],
  },
];

const bandFor = (score, max) => {
  const pct = score / max;
  if (pct < 0.4) return { band: "Getting started", copy: "There is real opportunity here. A focused AI Snapshot session would identify quick wins and a sensible starting point." };
  if (pct < 0.73) return { band: "Building momentum", copy: "You are on your way. An AI Roadmap would help you turn scattered use into practical, reusable workflows." };
  return { band: "AI ready", copy: "You are in a strong position. An AI Partner arrangement could help you build advanced systems and train the wider team." };
};

export const AIReadinessQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const max = QUESTIONS.length * 3;

  const choose = (qi, option) => {
    setAnswers((a) => ({ ...a, [qi]: option }));
  };

  const finish = async () => {
    const total = Object.values(answers).reduce((sum, o) => sum + o.score, 0);
    const b = bandFor(total, max);
    setSubmitting(true);
    try {
      await submitQuiz({
        answers: QUESTIONS.map((q, i) => ({
          question: q.q,
          answer: answers[i]?.label || "",
          score: answers[i]?.score || 0,
        })),
        total_score: total,
        max_score: max,
        band: b.band,
      });
    } catch {
      // submission failure should not block the result
    } finally {
      setSubmitting(false);
      setResult({ total, ...b });
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
  };

  if (result) {
    return (
      <div data-testid="quiz-result" className="rounded-3xl p-8 md:p-12" style={{ background: "#ECE9FF" }}>
        <span className="td-label block mb-4">Your Result</span>
        <div className="flex items-baseline gap-3 mb-4">
          <span className="font-head text-6xl font-semibold text-[#7C6CF0]">{result.total}</span>
          <span className="text-black/40 text-xl">/ {max}</span>
        </div>
        <h3 className="font-head text-2xl font-semibold mb-4 text-black">{result.band}</h3>
        <p className="text-[#4a4a55] leading-relaxed mb-8 max-w-lg">{result.copy}</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/services/ai-consultancy" className="td-btn td-btn-primary">
            Explore AI Consultancy <ArrowRight size={18} />
          </Link>
          <button onClick={reset} data-testid="quiz-reset" className="td-btn td-btn-outline">
            <RotateCcw size={16} /> Retake
          </button>
        </div>
      </div>
    );
  }

  const question = QUESTIONS[step];
  const isLast = step === QUESTIONS.length - 1;
  const answered = answers[step];

  return (
    <div data-testid="ai-readiness-quiz" className="bg-white rounded-3xl border border-[#e5e5e5] p-8 md:p-10">
      <div className="flex items-center justify-between mb-8">
        <span className="td-label inline-flex items-center gap-2">
          <Sparkles size={14} className="text-[#7C6CF0]" /> AI Readiness Quiz
        </span>
        <span className="text-sm text-[#8a8a8a]">{step + 1} / {QUESTIONS.length}</span>
      </div>

      <div className="h-1 w-full bg-[#f0f0f0] rounded-full mb-8 overflow-hidden">
        <div className="h-full bg-[#7C6CF0] transition-all duration-500" style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }} />
      </div>

      <h3 className="font-head text-xl md:text-2xl font-semibold mb-6">{question.q}</h3>
      <div className="space-y-3">
        {question.options.map((opt) => (
          <button
            key={opt.label}
            onClick={() => choose(step, opt)}
            data-testid={`quiz-option-${opt.score}`}
            className={`w-full text-left rounded-2xl border px-5 py-4 transition-colors ${
              answered?.label === opt.label
                ? "border-[#7C6CF0] bg-[#7C6CF0]/[0.05]"
                : "border-[#e5e5e5] hover:border-[#c0c0c0]"
            }`}
          >
            <span className="text-[#2b2b2b]">{opt.label}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mt-8">
        <button
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="inline-flex items-center gap-2 text-sm text-[#8a8a8a] hover:text-black transition-colors disabled:opacity-40"
        >
          <ArrowLeft size={16} /> Back
        </button>
        {isLast ? (
          <button onClick={finish} disabled={!answered || submitting} data-testid="quiz-submit" className="td-btn td-btn-primary disabled:opacity-50">
            {submitting ? "Calculating..." : "See my result"} <ArrowRight size={18} />
          </button>
        ) : (
          <button onClick={() => setStep((s) => s + 1)} disabled={!answered} data-testid="quiz-next" className="td-btn td-btn-primary disabled:opacity-50">
            Next <ArrowRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default AIReadinessQuiz;
